import React, { useEffect, useState } from "react";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import ManagerApprovalDialog from "./ManagerApprovalDialog";
import { REFUND_PAYOUT_TYPES } from "./RefundDialog";

/**
 * Refund a single line item (or a portion of it) off a past-business-day
 * order. Handles Package sub-services at their already-discounted rate
 * and hands off Edit-only requests to the Manager Approval popup.
 */
export default function LineItemRefundDialog({ open, onOpenChange, order, branch, itemIndex, onRefunded }) {
  const { staff } = useAuth();
  const canAuthorise = hasPermission(staff, "location.orders_pos.delete");
  const item = (order?.items || [])[itemIndex];
  const [payoutType, setPayoutType] = useState("cash");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [askGiftExpiry, setAskGiftExpiry] = useState(false);
  const [giftExpiry, setGiftExpiry] = useState("");
  const [approvalPayload, setApprovalPayload] = useState(null);
  // Server-authoritative refund cap for this line (fully-loaded, includes
  // its share of Service Charge + PPN + specials). Populated by
  // `GET /orders/{oid}/items/{idx}/refund-preview` when the dialog opens.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Amount the operator will actually be refunded. Editable — capped at
  // `preview.max_refund`. Empty string = auto (full-line refund).
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (!open || itemIndex == null || !order?.id) return;
    setPayoutType("cash");
    setNote("");
    setAskGiftExpiry(false);
    setPreview(null);
    setAmount("");
    const d = new Date();
    d.setMonth(d.getMonth() + 12);
    setGiftExpiry(d.toISOString().slice(0, 10));
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const r = await staffApi.get(`/orders/${order.id}/items/${itemIndex}/refund-preview`);
        if (cancelled) return;
        setPreview(r.data);
        // Seed the input with the full refund amount so a one-tap flow
        // still lands on the fully-loaded refund (previous behaviour).
        setAmount(String(Math.round(Number(r.data?.max_refund || 0))));
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, itemIndex, order?.id, order?.updated_at]);

  const maxRefund = Number(preview?.max_refund || 0);
  const parsedAmount = Number(amount);
  const amountValid = !isNaN(parsedAmount) && parsedAmount > 0 && parsedAmount <= maxRefund + 0.01;
  const isPartial = amountValid && parsedAmount < maxRefund - 0.01;
  // Mandatory reason — trim on read so a whitespace-only note doesn't
  // unlock Confirm. Keeps the audit trail useful and matches the
  // backend guard in `_perform_line_item_refund`.
  const reasonValid = note.trim().length > 0;

  const submitRefund = async (giftCardExpiresAt) => {
    if (!order?.id || itemIndex == null || !item) return;
    if (!amountValid) {
      toast.error(`Refund amount must be between 0 and ${formatBranchMoney(branch, maxRefund)}`);
      return;
    }
    if (!reasonValid) {
      toast.error("A reason for the refund is required.");
      return;
    }
    const body = { payout_type: payoutType, note: note.trim(), amount: Number(parsedAmount.toFixed(2)) };
    if (payoutType === "gift_card" && giftCardExpiresAt) body.gift_card_expires_at = giftCardExpiresAt;
    // Edit-only user → hand off to Manager Approval popup.
    if (!canAuthorise) {
      setApprovalPayload({ ...body, item_index: itemIndex });
      setAskGiftExpiry(false);
      return;
    }
    setBusy(true);
    try {
      const r = await staffApi.post(`/orders/${order.id}/items/${itemIndex}/refund`, body);
      if (r.data?.credited_points > 0) {
        toast.success(`${r.data.credited_points.toLocaleString()} points credited to the customer`);
      }
      if (r.data?.reversed_points > 0) {
        toast.info(`${r.data.reversed_points.toLocaleString()} earned pts deducted from wallet`);
      }
      onRefunded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); setAskGiftExpiry(false); }
  };

  const onConfirm = () => {
    if (!amountValid) {
      toast.error(`Refund amount must be between 0 and ${formatBranchMoney(branch, maxRefund)}`);
      return;
    }
    if (!reasonValid) {
      toast.error("A reason for the refund is required.");
      return;
    }
    if (payoutType === "gift_card") { setAskGiftExpiry(true); return; }
    submitRefund();
  };

  if (!item) return null;

  const clampToMax = () => {
    if (parsedAmount > maxRefund) setAmount(String(Math.round(maxRefund)));
  };

  return (
    <>
      <Dialog open={open && !approvalPayload} onOpenChange={onOpenChange}>
        <DialogContent data-testid="line-refund-dialog">
          <DialogHeader>
            <DialogTitle>Refund line item</DialogTitle>
            <DialogDescription>
              Refund all or part of the line <span className="font-medium">{item.name}</span> off order #{order?.order_number}. Service charge, PPN and earned loyalty recalculate on the actual amount refunded. Refunded product stock is <span className="font-medium">not</span> returned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Line total (incl. SC + PPN)</span><span data-testid="line-refund-max">{previewLoading ? "…" : formatBranchMoney(branch, maxRefund)}</span></div>
              {preview?.already_refunded > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Previously refunded on this line</span>
                  <span>{formatBranchMoney(branch, preview.already_refunded)}</span>
                </div>
              )}
              <div className="text-[10px] text-muted-foreground mt-1">Cap: original price + share of taxes.</div>
            </div>
            <div>
              <Label htmlFor="line-refund-amount-input">Amount to refund</Label>
              <Input
                id="line-refund-amount-input"
                type="number"
                min={0}
                step={1}
                max={maxRefund}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                onBlur={clampToMax}
                data-testid="line-refund-amount-input"
                placeholder={String(Math.round(maxRefund))}
                disabled={previewLoading || maxRefund <= 0}
              />
              <div className="flex items-center justify-between mt-1">
                <div className="text-[11px] text-muted-foreground">
                  {isPartial ? (
                    <>Partial refund — SC + PPN + loyalty will recompute on this amount.</>
                  ) : amountValid ? (
                    <>Full-line refund — line will be marked Refunded.</>
                  ) : (
                    <>Enter an amount between 1 and {formatBranchMoney(branch, maxRefund)}.</>
                  )}
                </div>
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
                  onClick={() => setAmount(String(Math.round(maxRefund)))}
                  disabled={previewLoading || maxRefund <= 0}
                  data-testid="line-refund-amount-max"
                >
                  Refund full amount
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="line-refund-payout-select">Refund to</Label>
              <div className="relative mt-1">
                <select
                  id="line-refund-payout-select"
                  value={payoutType}
                  onChange={(e) => setPayoutType(e.target.value)}
                  className="h-10 w-full cursor-pointer appearance-none rounded-md border border-input bg-background px-3 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  data-testid="line-refund-payout-select"
                >
                  {REFUND_PAYOUT_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
              </div>
            </div>
            <div>
              <Label htmlFor="line-refund-note-input">
                Reason for refund <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="line-refund-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Required — e.g. Client dissatisfied, wrong service, allergic reaction"
                data-testid="line-refund-note-input"
                aria-required="true"
                aria-invalid={!reasonValid}
              />
              {!reasonValid && (
                <p className="text-[11px] text-rose-600 mt-1" data-testid="line-refund-note-required">
                  A reason is required — one-time-only refund, logged in the audit trail.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="line-refund-cancel-btn">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={onConfirm} disabled={busy || !amountValid || !reasonValid} data-testid="line-refund-confirm-btn">
              {busy ? "Refunding…" : (canAuthorise ? (isPartial ? "Refund partial amount" : "Refund item") : "Request refund…")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManagerApprovalDialog
        open={!!approvalPayload}
        onOpenChange={(o) => { if (!o) setApprovalPayload(null); }}
        orderId={order?.id}
        kind="line_refund"
        payload={approvalPayload || {}}
        actionLabel={isPartial ? `Refund ${formatBranchMoney(branch, parsedAmount)} on "${item?.name}"` : `Refund line "${item?.name}"`}
        onApproved={() => { setApprovalPayload(null); onRefunded(); }}
        onQueued={() => { setApprovalPayload(null); onOpenChange(false); }}
      />

      <Dialog open={askGiftExpiry} onOpenChange={setAskGiftExpiry}>
        <DialogContent data-testid="line-refund-gift-expiry-dialog" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Gift Card Expiry</DialogTitle>
            <DialogDescription className="sr-only">Choose the validity date for the gift card issued as this refund.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="line-gift-expiry">Valid until</Label>
            <Input
              id="line-gift-expiry"
              type="date"
              value={giftExpiry}
              onChange={(e) => setGiftExpiry(e.target.value)}
              data-testid="line-refund-gift-expiry-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAskGiftExpiry(false)} data-testid="line-refund-gift-expiry-cancel">Back</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => submitRefund(giftExpiry)}
              disabled={busy || !giftExpiry}
              data-testid="line-refund-gift-expiry-confirm"
            >
              {busy ? "Issuing…" : "Issue Gift Card"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
